import type { EntityType } from "@config";
import type { ITableSettings } from "@library-elements/table";
import { applyFluidTypography, loadGoogleFontsForDesign } from "@/shared/utils";
import {
  applyBooleanCssTransforms,
  resolveDesignSystemVariables,
} from "@/shared/utils/resolve-design-system-variables";
import cloneDeep from "lodash/cloneDeep";
import type { StoryStateType } from "../types";
import { parseTextEntities } from "./parse-text-entities";

const handleDesignSystem = ({
  state,
  design,
  entities,
}: {
  state: StoryStateType;
  design: Record<string, unknown>;
  entities?: {
    [name: string]:
      | Record<string, unknown>
      | ((data: Record<string, unknown>) => Record<string, unknown>);
  };
}) => {
  const story = state.story;
  if (!story) return null;

  const designSystem: Record<string, unknown> = {};
  const originalEntities: Record<string, Record<string, unknown>> = {};
  // Post-mutation per-entity patches, collected for the collab broadcast: the
  // `entities` recipe can contain functions and runs against THIS client's
  // state, so peers can't re-apply it — they need the computed results.
  const appliedEntities: Record<string, Record<string, unknown>> = {};

  if (entities) {
    const flatLayers = Object.values(state.flatContentLayers);
    const entitiesNames = Object.keys(entities);

    flatLayers.forEach((item) => {
      if (
        item.type !== "element" ||
        !item.settings?.name ||
        typeof item.settings.name !== "string"
      )
        return;
      const name = item.settings.name;
      if (!entitiesNames.includes(name)) return;

      let settingsUpdate: Record<string, unknown>;
      if (typeof entities[name] === "function") {
        settingsUpdate = (entities[name] as Function)(item);
      } else settingsUpdate = entities[name] as Record<string, unknown>;

      if (["RichText", "CollapsibleText"].includes(name)) {
        const data = parseTextEntities(
          item.settings.content,
          settingsUpdate,
        );

        if (data) {
          item.settings.content = data.updatedContent;
          window.__CONTENT_TRIGGER_UPDATE__?.(item.id, data.updatedContent);
          Object.assign(originalEntities, {
            [item.id]: {
              content: data.originalContent,
            },
          });
          appliedEntities[item.id] = { content: data.updatedContent };
        }
        // Apply any non-content keys from `settingsUpdate` to the entity's
        // settings object too. parseTextEntities only mutates `content`; if
        // a caller passes additional persisted-property keys (e.g. tracking
        // flags) they'd be silently dropped without this assign.
        const { content: _ignored, ...rest } = settingsUpdate as Record<
          string,
          unknown
        >;
        if (Object.keys(rest).length > 0) {
          Object.assign(item.settings, rest);
          appliedEntities[item.id] = { ...appliedEntities[item.id], ...rest };
        }
        return;
      }

      if (name === "Table") {
        const ogEntities = handleParseTableEntitiy(item, settingsUpdate);
        if (ogEntities) {
          Object.assign(originalEntities, ogEntities);
          appliedEntities[item.id] = { tableData: item.settings.tableData };
        }
        return;
      }

      // Store original settings
      originalEntities[item.id] = Object.fromEntries(
        Object.keys(settingsUpdate).map((key) => [key, item.settings[key]]),
      );

      // Update settings
      Object.assign(item.settings, settingsUpdate);
      appliedEntities[item.id] = settingsUpdate;
    });
  }

  Object.keys(design).forEach((key) => {
    designSystem[key] = story.settings.designSystemV2[key];
  });
  Object.assign(story.settings.designSystemV2, design);

  // The design-system-dialog Save flow (and brand-imports) can introduce
  // brand-new font-family names that weren't on the page when the story
  // first loaded. handle-design's <link> injection only runs once at story
  // load via use-get-story.tsx, so newly chosen fonts (e.g. Roboto picked
  // for headingFont after a brand import) never get their Google Fonts CSS
  // requested. Re-run the link-injection step against the merged design so
  // every current font has a stylesheet in document.head. The helper is
  // idempotent — it short-circuits when the URL already exists.
  loadGoogleFontsForDesign(
    story.settings.designSystemV2 as Record<string, unknown>,
  );

  // Sync CSS variables on the editor root. We resolve against the FULL
  // merged design system (not just the `design` diff) so the hover
  // fallback logic inside `resolveDesignSystemVariables` can always
  // reach the paired base color — otherwise an update that only
  // contains a hover key (e.g. the user deleted the button text hover)
  // would skip the derivation because its base isn't in the diff.
  const root = document.documentElement;
  const mergedDesign = story.settings.designSystemV2 as Record<string, unknown>;
  const setProperty = (cssVar: string, value: string) =>
    root.style.setProperty(cssVar, value);

  for (const [cssVar, cssValue] of resolveDesignSystemVariables(mergedDesign)) {
    setProperty(cssVar, cssValue);
  }

  // Boolean CSS variables (grayscale etc.) only applied when the key is in
  // the diff so unrelated updates don't reset the grayscale filter. Shared
  // helper keeps the transform table in sync with the editor path.
  applyBooleanCssTransforms(design, setProperty);

  // Re-wrap the font-size vars we just set as plain px into fluid clamp()s so
  // mobile keeps fluid typography after any design-system change. Idempotent
  // (clamp max is recovered on re-run) and a no-op in PDF export.
  applyFluidTypography();

  return { designSystem, originalEntities, appliedEntities };
};

export default handleDesignSystem;

// ============== HELPER FUNCTIONS ==============
const handleParseTableEntitiy = (
  entity: EntityType,
  payload: Record<string, unknown>,
) => {
  const { tableData } = entity.settings as unknown as ITableSettings;
  const ogTableData = cloneDeep(tableData);

  for (const cell of tableData) {
    const data = parseTextEntities(cell.content, payload);
    if (data) {
      cell.content = data.updatedContent;
      window.__CONTENT_TRIGGER_UPDATE__?.(cell.id, data.updatedContent);
    }
  }

  return { [entity.id]: { tableData: ogTableData } };
};

// ============== HELPER FUNCTIONS ==============
